import React, { useState, useEffect } from 'react';
import { useIntl, useHistory, getLocale } from 'umi';
import { PagingQuery } from 'tea-component';
import BubbleClose from '@intelligence/components/BubbleClose';
import FirstRow from '@intelligence/components/TableComponents/components/FirstRow';
import { getCountryFlag } from '@home/_config/countries';
import { TopchartTableProps } from '@intelligence/pages/_config/topchartsInterface';
import {
  ActiveUserMetricColumn,
  dauTitle,
  revenueTitle,
  downloadTitle,
  arpuTitle,
} from '@intelligence/pages/TopCharts/_config/TableCloums/ActiveUsersCloums';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { dbiBtnLogMes } from '@intelligence/utils/buriedPointCfg';
import Databarin from '@intelligence/assets/svg/dataSource/databarin.svg';
import DatabrainBubbleTip from '@intelligence/components/TableComponents/components/FirstRow/DatabrainBubbleTip';
import TopchartTable from '@intelligence/components/TableComponents/TopchartTable';
import {
  TableCheckboxHeader,
  TableCheckboxRender,
} from '@intelligence/components/TableComponents/components/TableCheckbox';
import { getDetailGamePathCommon } from '@intelligence/utils/topchartsUtils';
import CommonColumns, { DetailsCloums } from '@intelligence/pages/TopCharts/_config/TableCloums/CommonCloums';
import { valueFormat, valueFormatArpu, growthFormat } from '@intelligence/utils/commonUtils';
import { LANGUAGE_CN } from '@home/_config/config';
import { getGameRankDetails } from '@intelligence/utils/tableUtils';
import { getGenreColumns } from '@intelligence/v3/utils/topcharts';
import { getTagFilterColumns } from '@intelligence/v3/utils/sensorTowerTagColumns';
import { AppMagicGenreTagType } from '@intelligence/components/TableComponents/components/AppMagicGenreTagList';
import { getMetricCompareValues } from './utils';

import type { SortByItemType } from '@intelligence/components/TableComponents/components/SortBy';
import type { IDataCompareData, IDataCompareHelper } from '@intelligence/v3/components/DataCompareBubble';
import type { IOptionWithChildren } from '@intelligence/components/GenreSelect/types';

interface MultiDataSourceTopChartsTableTableProps extends TopchartTableProps {
  showFirstGuide: boolean;
  selectDataSources: string[];
  entityType?: string;
  dateType: string;
  startDate?: string;
  endDate?: string;
  setMetricCloumsIndex?: any;
  topChartsType: 'activeuser' | 'revenue' | 'downloads' | 'databrain';
  genreSource: IGenreSource;
  genreOptionsMap?: IRecord<IOptionWithChildren[]>;
  onClick?: (url: string) => Promise<void>;
  // 是否加载游戏详情数据中
  gameDetailsLoading: boolean;
  // 游戏详情数据
  gameDetailsMapping: IRecord<IRecord>;
  // App Magic 品类标签点击回调
  onAppMagicTagClick?: (
    type: AppMagicGenreTagType,
    superTag: string,
    majorTag?: string,
    midTag?: string,
    subTag?: string,
  ) => void;
  // Tag 点击回调
  onTagClick?: (type: string, tag: string) => void;
  /** 当前筛选器值，用于动态展示 Sensor Tower / App Magic 标签列 */
  filterValue?: IRecord<any>;
}
export default function MultiDataSourceTopChartsTable(props: MultiDataSourceTopChartsTableTableProps) {
  const {
    showFirstGuide,
    loading,
    records = [],
    tableTotal = 0,
    pageSortAttr,
    tableSelection,
    tableSelectionAll,
    onTableSortChage,
    onTablePagingChange,
    onTableCheckGame,
    selectDataSources,
    topChartsType,
    entityType = '',
    dateType,
    startDate,
    endDate,
    genreSource = 'sensortower',
    genreOptionsMap,
    onClick,
    gameDetailsLoading,
    gameDetailsMapping,
    onAppMagicTagClick,
    onTagClick,
    filterValue,
  } = props;

  const { formatMessage } = useIntl(); // 中英切换方法
  const locale = getLocale();

  const { tablePageIndex, tablePageSize, currentSortValue, currentSortMetric, currentRatioType, currentOrderSource } =
    pageSortAttr || {};
  const history = useHistory();
  // const defaultPageSize = 100;
  const defaultPageSize = 50;
  const [pageIndex, setPageIndex] = useState(tablePageIndex || 1);
  const [pageSize, setPageSize] = useState(tablePageSize || defaultPageSize);
  const [selectList, setSelectList] = useState(tableSelection);
  const [bubbleVisible, setBubbleVisible] = useState(false);

  useEffect(() => {
    setPageIndex(tablePageIndex);
    setPageSize(tablePageSize);
  }, [tablePageIndex, tablePageSize]);

  const onPagingChange = (e: PagingQuery) => {
    setPageIndex(e.pageIndex || 1);
    setPageSize(e.pageSize || defaultPageSize);
    onTablePagingChange(e);
  };

  const onSortChage = (e: SortByItemType) => {
    onTableSortChage(e);
  };

  useEffect(() => {
    setSelectList(tableSelection);
  }, [tableSelection]);

  const getMetricValueFormatter = (metric: string): IDataCompareHelper['valueFormatter'] => {
    return (rawValue) => {
      const numValue = Number(rawValue);
      if (metric === 'arpu') {
        return String(valueFormatArpu(numValue, false, 0));
      }
      return String(valueFormat(numValue));
    };
  };

  const getMetricValueOfCvm = (cvm: any, sortMetric: string, dataSource: string) => {
    const {
      metricStd: sortMetricStd,
      value: metricValue,
      ratioValue: metricRatioValue,
      prevValue: prevMetricValue,
    } = getMetricCompareValues(cvm, dataSource, sortMetric, dateType as IGranularity);
    const value = sortMetricStd === 'arpu' ? valueFormatArpu(metricValue, false, 0) : valueFormat(metricValue);
    const growthDataObj = growthFormat({
      value: metricRatioValue,
      valueType: 'pointValue',
      hasPoint: 'lessThanOne',
      fixedNum: 2,
      maxNumber: 99999,
    });
    let dataCompareData: (IDataCompareData & IDataCompareHelper) | undefined;

    if (startDate && endDate && Number.isFinite(metricValue) && Number.isFinite(prevMetricValue)) {
      dataCompareData = {
        entityType: entityType || 'mobile',
        icon: cvm?.cover,
        title: cvm?.game_name,
        granularity: dateType as IGranularity,
        startDate,
        endDate,
        value: metricValue,
        prevValue: prevMetricValue,
        valueFormatter: getMetricValueFormatter(sortMetricStd),
      };
    }
    return { value, growthDataObj, dataCompareData };
  };

  const getMetricCloums = (dataSource: string[]) => {
    const metricCloums: any[] = [];
    // DAU Revenue Download 每个数据源赋值一遍
    const titles = [];
    if (topChartsType === 'activeuser') {
      titles.push(dauTitle(dateType), revenueTitle, arpuTitle, downloadTitle);
    } else if (topChartsType === 'databrain') {
      titles.push(dauTitle(dateType), revenueTitle, downloadTitle);
    } else if (topChartsType === 'revenue') {
      titles.push(revenueTitle);
      titles.push(arpuTitle);
    } else if (topChartsType === 'downloads') {
      titles.push(downloadTitle);
    }
    // console.log(titles);
    // console.log(revenueTitle, arpuTitle)

    titles.forEach((t) => {
      dataSource.forEach((source) => {
        metricCloums.push(
          ActiveUserMetricColumn({
            dataSource: source, // source === 'ogdb' ? 'databarin' : source,
            title: locale === LANGUAGE_CN ? t?.title_cn || t?.title : t?.title,
            title_cn: t?.title_cn,
            title_en: t?.title,
            textContent: t.textContent,
            key: `${t.key}_${source}`,
            sortMetric: t.key,
            // @ts-ignore
            metricKey: t?.metricKey || t.key,
            currentSortValue,
            currentSortKey: `${currentSortMetric}_${currentOrderSource}_${currentRatioType}`,
            onSortChage,
            // @ts-ignore
            width: t?.width,
            // @ts-ignore
            width_cn: t?.width_cn,
            getMetricValueOfCvm,
            entityType,
          }),
        );
      });
    });
    return metricCloums;
  };

  useEffect(() => {
    setBubbleVisible(showFirstGuide && !loading && records.length > 0);
  }, [showFirstGuide, loading, records.length]);

  const onBubbleClose = () => {
    // 表格关注提示关闭打点
    buriedBtnPoint(dbiBtnLogMes.topchartMobileGameActiveUserTableTip);
    setBubbleVisible(false);
  };

  const getDetailGamePath = (gameId: string) => {
    return getDetailGamePathCommon({
      entityType: 'mobile',
      gameId,
      tab: 'stats',
      subTab: 'activeusers',
    });
  };

  const onClickGame = (gameId: string) => {
    const url = getDetailGamePath(gameId);

    if (onClick) {
      onClick(url);
      return;
    }

    history.push(url);
  };

  return (
    <>
      <TopchartTable
        // @ts-ignore
        loading={loading}
        recordKey={'id'}
        records={records}
        tableTotal={tableTotal}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPagingChange={onPagingChange}
        columns={[
          // { key: 'select', header: null, width: 37, fixed: 'left' },
          {
            key: 'select',
            width: 36,
            fixed: 'left',
            header: () => {
              return (
                <TableCheckboxHeader
                  selectList={selectList}
                  tableSelectionAll={tableSelectionAll}
                  disabled={loading || records.length === 0}
                  onChange={(list, stats) => {
                    onTableCheckGame(list, stats);
                    setSelectList(list);
                  }}
                />
              );
            },
            render: (cvm: any) => {
              return (
                <TableCheckboxRender
                  selectList={selectList}
                  cvm={{ ...(cvm || {}), gameId: cvm?.game_id || '' }}
                  disabled={getGameRankDetails(cvm?.rank).isDisabled}
                  onChange={(list, status) => {
                    setSelectList(list);
                    onTableCheckGame(list, status);
                  }}
                />
              );
            },
          },
          {
            key: 'gametitle',
            header: (
              <BubbleClose
                style={{ width: '400px' }}
                placement="bottom-start"
                placementOffset={[60, -35]}
                content={formatMessage({ id: `dbi_first_follow_info` })}
                visible={bubbleVisible}
                onClose={onBubbleClose}
              >
                <div className="dbi-firstRow-flex">
                  <div className="dbi-text-id">#</div>
                  <div>{formatMessage({ id: 'dbi_top_gametitle' })}</div>
                </div>
              </BubbleClose>
            ),
            width: 330,
            fixed: 'left',
            render: (cvm: any) => {
              const gameId = cvm?.game_id || '';
              const gameDetails = gameDetailsLoading ? {} : gameDetailsMapping?.[gameId] || {};

              return (
                <FirstRow
                  id={getGameRankDetails(cvm?.rank)?.rankText}
                  cvm={{
                    img: cvm?.cover,
                    gametitle: cvm?.game_name,
                    countryIcon: gameDetailsLoading ? '' : getCountryFlag(gameDetails?.country_abbre),
                    text: gameDetails?.publisher,
                    isDatabrain: gameDetails?.b_ogdb_data && topChartsType !== 'databrain' ? Databarin : '',
                    databrainIconTip: <DatabrainBubbleTip />,
                  }}
                  rihgtClickToJumpHref={getDetailGamePath(gameId)}
                  onClick={() => {
                    onClickGame(gameId);
                  }}
                />
              );
            },
          },
          ...getMetricCloums(selectDataSources),
          CommonColumns.ReleaseTimeColumn({
            locale,
            formatMessage,
            isGameDetailsMode: true,
            gameDetailsLoading,
            gameDetailsMapping,
          }),
          // 根据 genreSource 决定展示 Genre 列还是 App Magic 品类列
          ...getGenreColumns({
            gameDetailsLoading,
            gameDetailsMapping,
            genreSource,
            genreOptionsMap,
            locale,
            formatMessage,
            onAppMagicTagClick,
          }),
          ...getTagFilterColumns({
            filterValue,
            gameDetailsLoading,
            gameDetailsMapping,
            locale,
            formatMessage,
            onTagClick,
          }),
          CommonColumns.GameEngineColumn({
            formatMessage,
            isGameDetailsMode: true,
            gameDetailsLoading,
            gameDetailsMapping,
            onTagClick,
          }),
          DetailsCloums({
            onClickGame: (cvm: any) => {
              onClickGame(cvm?.game_id);
            },
            formatMessage,
          }),
        ]}
      />
    </>
  );
}
